import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LegalHRPDFExport from '../../components/legal-hr/LegalHRPDFExport';
import toast from 'react-hot-toast';
import { anthropicService } from '../../services/anthropicService';
import { supabase } from '../../lib/supabase';
import { legalHrStorageService, SavedLegalHrDocument } from '../../services/legalHrStorageService';

// Define supported countries for contractor agreement
const supportedCountries = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'EU', name: 'European Union', flag: '🇪🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

// Define business types for dropdown
const businessTypes = [
  'Technology Company',
  'Retail Business',
  'Professional Services',
  'Healthcare Organization',
  'Manufacturing Company',
  'Hospitality Business',
  'Educational Institution',
  'Non-profit Organization',
  'Financial Services',
  'Creative Agency',
];

// Define contractor service types
const serviceTypes = [
  'Software Development',
  'Design & Creative',
  'Marketing & Advertising',
  'Writing & Content Creation',
  'Consulting Services',
  'Administrative Support',
  'Technical Support',
  'Financial Services',
  'Legal Services',
  'Training & Education',
  'Other'
];

// Define agreement clauses
const agreementClauses = [
  { id: 'scope', label: 'Scope of Services', example: 'Detailed description of the services to be provided', required: true },
  { id: 'payment', label: 'Payment Terms', example: 'Rates, schedule, invoicing, and payment methods', required: true },
  { id: 'term', label: 'Term and Termination', example: 'Duration of agreement and termination conditions', required: true },
  { id: 'independent', label: 'Independent Contractor Status', example: 'Clarification of non-employee relationship', defaultSelected: true },
  { id: 'intellectual', label: 'Intellectual Property Rights', example: 'Ownership of created works and materials', defaultSelected: true },
  { id: 'confidentiality', label: 'Confidentiality', example: 'Protection of business information and trade secrets', defaultSelected: true },
  { id: 'liability', label: 'Limitation of Liability', example: 'Limits on potential damages and liabilities', defaultSelected: true },
  { id: 'indemnification', label: 'Indemnification', example: 'Protection against third-party claims', defaultSelected: false },
  { id: 'insurance', label: 'Insurance Requirements', example: 'Types and amounts of required insurance', defaultSelected: false },
  { id: 'modification', label: 'Modification Clause', example: 'Process for changing the agreement', defaultSelected: false },
  { id: 'noncompete', label: 'Non-Compete Clause', example: 'Restrictions on competing activities', defaultSelected: false },
  { id: 'deliverables', label: 'Deliverables & Milestones', example: 'Specific work products and timeline', defaultSelected: true },
  { id: 'expenses', label: 'Expenses', example: 'Which expenses are reimbursable and process', defaultSelected: false },
  { id: 'dispute', label: 'Dispute Resolution', example: 'Process for resolving disagreements', defaultSelected: true },
];

const ContractorAgreementGenerator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [agreementGenerated, setAgreementGenerated] = useState(false);
  
  // Form state
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [contractorName, setContractorName] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [customServiceType, setCustomServiceType] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('GB');
  const [selectedClauses, setSelectedClauses] = useState<string[]>(
    agreementClauses.filter(clause => clause.defaultSelected || clause.required).map(clause => clause.id)
  );
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [agreementText, setAgreementText] = useState('');
  const [latestDocument, setLatestDocument] = useState<SavedLegalHrDocument | null>(null);
  const [agreementDuration, setAgreementDuration] = useState('');

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const handleClauseChange = (clauseId: string) => {
    // Don't allow deselection of required clauses
    if (agreementClauses.find(clause => clause.id === clauseId)?.required) {
      return;
    }
    
    if (selectedClauses.includes(clauseId)) {
      setSelectedClauses(selectedClauses.filter(item => item !== clauseId));
    } else {
      setSelectedClauses([...selectedClauses, clauseId]);
    }
  };

  const generateAgreement = async () => {
    // Validation
    if (!clientName) {
      toast.error('Please enter client name');
      return;
    }
    
    if (!contractorName) {
      toast.error('Please enter contractor name');
      return;
    }
    
    if (!businessType) {
      toast.error('Please select your business type');
      return;
    }
    
    if (!serviceType) {
      toast.error('Please select a service type');
      return;
    }
    
    if (serviceType === 'Other' && !customServiceType) {
      toast.error('Please specify the custom service type');
      return;
    }
    
    if (!projectDescription) {
      toast.error('Please enter a project description');
      return;
    }
    
    if (!paymentAmount) {
      toast.error('Please specify the payment amount');
      return;
    }
    
    if (!paymentTerms) {
      toast.error('Please specify the payment terms');
      return;
    }
    
    if (!clientEmail) {
      toast.error('Please enter a client email');
      return;
    }
    
    if (!agreementDuration) {
      toast.error('Please specify the agreement duration');
      return;
    }

    setGenerating(true);
    
    try {
      // Get the country name from the selected country code
      const selectedCountryData = supportedCountries.find(country => country.code === selectedCountry);
      const countryName = selectedCountryData ? selectedCountryData.name : 'United Kingdom';
      
      // Get current user ID from Supabase
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Format the selected clauses into a readable list
      const selectedAgreementClauses = agreementClauses
        .filter(clause => selectedClauses.includes(clause.id))
        .map(clause => clause.label)
        .join(', ');

      // Determine the actual service type (either selected or custom)
      const finalServiceType = serviceType === 'Other' ? customServiceType : serviceType;

      // Create a comprehensive prompt for Anthropic
      const prompt = `
Generate a comprehensive and professional Independent Contractor Agreement between "${clientName}" (Client) and "${contractorName}" (Contractor) for ${finalServiceType} services.

The agreement should be governed by the laws of ${countryName} and include the following sections: ${selectedAgreementClauses}

Key details:
- Client business type: ${businessType}
- Service/Project description: ${projectDescription}
- Payment amount: ${paymentAmount}
- Payment terms: ${paymentTerms}
- Client email: ${clientEmail}
- Agreement duration: ${agreementDuration}
- Effective date: ${effectiveDate}

Please format the agreement in markdown, with appropriate sections, headings, and comprehensive coverage of all required elements for ${countryName}.

Make sure the agreement complies with independent contractor laws specific to ${countryName} such as ${
  selectedCountry === 'GB' ? 'UK employment classification laws, IR35 rules, and tax regulations' : 
  selectedCountry === 'US' ? 'IRS classification guidelines, state-specific contractor laws' : 
  selectedCountry === 'EU' ? 'relevant EU member state employment classification rules' : 
  selectedCountry === 'CA' ? 'Canadian tax laws regarding independent contractors' : 
  selectedCountry === 'AU' ? 'Australian Taxation Office rules on independent contractors' : 
  'relevant independent contractor classification laws'
}.

The agreement should be written in clear, legally sound language and formatted for professional use. Include appropriate signature blocks at the end.
`;

      // Make the actual API call to Anthropic through our service
      const response = await anthropicService.generateText(prompt);
      
      setAgreementText(response);
      setLatestDocument(null);
      setAgreementGenerated(true);
      toast.success('Contractor Agreement generated successfully!');
    } catch (error) {
      console.error('Error generating contractor agreement:', error);
      toast.error('Failed to generate contractor agreement. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (agreementText) {
      navigator.clipboard.writeText(agreementText)
        .then(() => toast.success('Contractor Agreement copied to clipboard'))
        .catch((err) => {
          console.error('Failed to copy:', err);
          toast.error('Failed to copy text');
        });
    } else {
      toast.error('No content to copy');
    }
  };

  const handlePdfExportComplete = async ({ blob }: { filename: string; blob: Blob }) => {
    if (!user?.id) {
      toast.error('You must be logged in to save the agreement');
      return;
    }

    try {
      const resolvedServiceType = serviceType === 'Other' ? customServiceType : serviceType;
      const saved = await legalHrStorageService.saveLegalHrDocument({
        userId: user.id,
        docType: 'contractor-agreement',
        blob,
        baseName: clientName || contractorName || resolvedServiceType || 'contractor-agreement',
        effectiveDate,
        metadata: {
          clientName,
          contractorName,
          businessType,
          serviceType: resolvedServiceType,
          projectDescription,
          paymentAmount,
          paymentTerms,
          clientEmail,
          agreementDuration,
          jurisdiction: selectedCountry,
          selectedClauses,
        },
        tags: [
          `jurisdiction:${selectedCountry}`,
          resolvedServiceType ? `service-type:${resolvedServiceType}` : 'service-type:unspecified',
          agreementDuration ? `agreement-duration:${agreementDuration}` : 'agreement-duration:unspecified',
        ],
      });

      setLatestDocument(saved);
      toast.success(`Contractor Agreement saved to workspace as version v${saved.version}`);
    } catch (error) {
      console.error('Failed to upload Contractor Agreement PDF', error);
      toast.error('Failed to save Contractor Agreement to storage');
    }
  };



  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with gradient */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-teal-600">
            Contractor Agreement Generator
          </h1>
          <p className="mt-3 text-xl text-gray-600 max-w-2xl mx-auto">
            Create a legally sound independent contractor agreement tailored to your specific business needs.
          </p>
        </div>

        {/* Main content */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {!agreementGenerated ? (
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column - Party Info */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Agreement Information</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-1">
                        Client Name/Company*
                      </label>
                      <input
                        type="text"
                        id="clientName"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        placeholder="Client Company Ltd"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contractorName" className="block text-sm font-medium text-gray-700 mb-1">
                        Contractor Name/Company*
                      </label>
                      <input
                        type="text"
                        id="contractorName"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        placeholder="Contractor Name or Company"
                        value={contractorName}
                        onChange={(e) => setContractorName(e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                        Client Business Type*
                      </label>
                      <select
                        id="businessType"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                      >
                        <option value="">Select a business type</option>
                        {businessTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-1">
                        Service Type*
                      </label>
                      <select
                        id="serviceType"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        value={serviceType}
                        onChange={(e) => setServiceType(e.target.value)}
                      >
                        <option value="">Select a service type</option>
                        {serviceTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {serviceType === 'Other' && (
                      <div>
                        <label htmlFor="customServiceType" className="block text-sm font-medium text-gray-700 mb-1">
                          Custom Service Type*
                        </label>
                        <input
                          type="text"
                          id="customServiceType"
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                          placeholder="Specify service type"
                          value={customServiceType}
                          onChange={(e) => setCustomServiceType(e.target.value)}
                        />
                      </div>
                    )}
                    
                    <div>
                      <label htmlFor="projectDescription" className="block text-sm font-medium text-gray-700 mb-1">
                        Project/Service Description*
                      </label>
                      <textarea
                        id="projectDescription"
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        placeholder="Describe the project or services to be provided"
                        value={projectDescription}
                        onChange={(e) => setProjectDescription(e.target.value)}
                      ></textarea>
                    </div>
                    
                    <div>
                      <label htmlFor="paymentAmount" className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Amount*
                      </label>
                      <input
                        type="text"
                        id="paymentAmount"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        placeholder="£50/hour, £5,000 flat fee, etc."
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Terms*
                      </label>
                      <select
                        id="paymentTerms"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        value={paymentTerms}
                        onChange={(e) => setPaymentTerms(e.target.value)}
                      >
                        <option value="">Select payment terms</option>
                        <option value="Upon completion">Upon completion</option>
                        <option value="Net 15 days">Net 15 days</option>
                        <option value="Net 30 days">Net 30 days</option>
                        <option value="Net 60 days">Net 60 days</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Bi-weekly">Bi-weekly</option>
                        <option value="Weekly">Weekly</option>
                        <option value="50% upfront, 50% upon completion">50% upfront, 50% upon completion</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Right column - Agreement Details */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Agreement Details</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="clientEmail" className="block text-sm font-medium text-gray-700 mb-1">
                        Client Email*
                      </label>
                      <input
                        type="email"
                        id="clientEmail"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        placeholder="client@example.com"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                        Governing Jurisdiction*
                      </label>
                      <select
                        id="country"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                      >
                        {supportedCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Effective Date
                      </label>
                      <input
                        type="date"
                        id="effectiveDate"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="agreementDuration" className="block text-sm font-medium text-gray-700 mb-1">
                        Agreement Duration*
                      </label>
                      <select
                        id="agreementDuration"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        value={agreementDuration}
                        onChange={(e) => setAgreementDuration(e.target.value)}
                      >
                        <option value="">Select duration</option>
                        <option value="Project completion">Project completion</option>
                        <option value="1 month">1 month</option>
                        <option value="3 months">3 months</option>
                        <option value="6 months">6 months</option>
                        <option value="1 year">1 year</option>
                        <option value="2 years">2 years</option>
                        <option value="Until terminated">Until terminated</option>
                      </select>
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Select clauses to include in your agreement:
                      </label>
                      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                        {agreementClauses.map((clause) => (
                          <div key={clause.id} className="flex items-start">
                            <div className="flex items-center h-5">
                              <input
                                id={clause.id}
                                type="checkbox"
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                checked={selectedClauses.includes(clause.id)}
                                onChange={() => handleClauseChange(clause.id)}
                                disabled={clause.required}
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label htmlFor={clause.id} className="font-medium text-gray-700 flex items-center">
                                {clause.label}
                                {clause.required && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                                    Required
                                  </span>
                                )}
                              </label>
                              <p className="text-gray-500">{clause.example}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Generate button */}
              <div className="mt-8 flex justify-center">
                <button
                  onClick={generateAgreement}
                  disabled={generating}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    'Generate Contractor Agreement'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <div className="mb-6 flex justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Generated Contractor Agreement</h2>
                <div className="space-x-2">
                  <button 
                    onClick={handleCopyToClipboard}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Copy to Clipboard
                  </button>
                  {agreementText && (
                    <LegalHRPDFExport 
                      contentRef={contentRef} 
                      filename="contractor-agreement.pdf"
                      title={clientName ? `${clientName} - Contractor Agreement` : "Contractor Agreement"}
                      documentType="Contractor Agreement"
                      onExportComplete={handlePdfExportComplete}
                    />
                  )}
                  <button 
                    onClick={() => setAgreementGenerated(false)}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Edit
                  </button>
                </div>
              </div>
              
              {/* Display the generated agreement in a nicely formatted way */}
              <div className="bg-white p-6 rounded-lg shadow-md" ref={contentRef}>
                <div dangerouslySetInnerHTML={{ 
                  __html: agreementText
                    .replace(/^# (.*)/gm, '<h1 class="text-3xl font-bold mb-4">$1</h1>')
                    .replace(/^## (.*)/gm, '<h2 class="text-2xl font-semibold mt-6 mb-3">$1</h2>')
                    .replace(/^### (.*)/gm, '<h3 class="text-xl font-medium mt-4 mb-2">$1</h3>')
                    .replace(/\n\n/g, '<p class="mb-4"></p>') 
                }} />
              </div>
              
              <div className="mt-8 text-center">
                {latestDocument?.signedUrl && (
                  <div className="mb-4">
                    <a
                      href={latestDocument.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 underline"
                    >
                      View saved Contractor Agreement (v{latestDocument.version})
                    </a>
                  </div>
                )}
                <div className="bg-yellow-50 p-4 border border-yellow-100 rounded-md mb-4">
                  <p className="text-sm text-yellow-700">
                    <strong>Legal Disclaimer:</strong> This Contractor Agreement is generated by AI and should be reviewed by a qualified legal professional before use. Laws regarding independent contractors vary by jurisdiction and may change over time. The generated document may not cover all legal requirements specific to your business or jurisdiction.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setAgreementGenerated(false);
                    window.scrollTo(0, 0);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Generate Another Agreement
                </button>
              </div>
            </div>
          )}
          
          {/* Disclaimer */}
          <div className="bg-yellow-50 p-4 border-t border-yellow-100">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Legal Disclaimer</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This generator provides a template only and is not a substitute for professional legal advice. 
                    The generated agreement should be reviewed by qualified legal professionals before signing.
                    Improper classification of workers can result in significant legal and tax consequences.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorAgreementGenerator;
